import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'

import { ThemeProvider } from './components/ThemeProvider'
import { AuthProvider } from './components/AuthProvider'
import AppShell from './components/AppShell'
import ProtectedRoute from './components/ProtectedRoute'
import RequirePermission from './components/RequirePermission'

// Lazy load pages for better performance
import { lazy, Suspense } from 'react'
import { PERMS } from './lib/permissions'

// Layout
const PatientChartLayout = lazy(() => import('./components/PatientChartLayout'))
const PortalLayout = lazy(() => import('./components/PortalLayout'))

// Existing pages
const Login = lazy(() => import('./pages/Login'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const PatientRegistration = lazy(() => import('./pages/PatientRegistration'))
const OrderEntry = lazy(() => import('./pages/OrderEntry'))
const ResultsReview = lazy(() => import('./pages/ResultsReview'))
const MedicationMAR = lazy(() => import('./pages/MedicationMAR'))
const ClinicalNotes = lazy(() => import('./pages/ClinicalNotes'))
const Appointments = lazy(() => import('./pages/Appointments'))
const AppointmentForm = lazy(() => import('./pages/AppointmentForm'))

// Patient module pages
const PatientSearch = lazy(() => import('./pages/PatientSearch'))
const PatientMerge = lazy(() => import('./pages/PatientMerge'))
const PatientDemographics = lazy(() => import('./pages/PatientDemographics'))
const PatientEncounters = lazy(() => import('./pages/PatientEncounters'))
const PatientAllergies = lazy(() => import('./pages/PatientAllergies'))
const PatientProblems = lazy(() => import('./pages/PatientProblems'))
const PatientImmunizations = lazy(() => import('./pages/PatientImmunizations'))
const PatientInsurance = lazy(() => import('./pages/PatientInsurance'))
const PatientProxies = lazy(() => import('./pages/PatientProxies'))
const PatientAllergyForm = lazy(() => import('./pages/PatientAllergyForm'))
const PatientProblemForm = lazy(() => import('./pages/PatientProblemForm'))
const PatientImmunizationForm = lazy(() => import('./pages/PatientImmunizationForm'))
const PatientInsuranceForm = lazy(() => import('./pages/PatientInsuranceForm'))
const PatientContactForm = lazy(() => import('./pages/PatientContactForm'))
const IntakeOutput = lazy(() => import('./pages/IntakeOutput'))
const CarePlanManagement = lazy(() => import('./pages/CarePlanManagement'))
const FamilyHistoryTab = lazy(() => import('./pages/FamilyHistoryTab'))
const SocialHistoryTab = lazy(() => import('./pages/SocialHistoryTab'))
const SurgicalHistoryTab = lazy(() => import('./pages/SurgicalHistoryTab'))
const ReferralManagement = lazy(() => import('./pages/ReferralManagement'))
const AdvanceDirectivesTab = lazy(() => import('./pages/AdvanceDirectivesTab'))
const PatientIdentifiersTab = lazy(() => import('./pages/PatientIdentifiersTab'))
const PatientAddressesTab = lazy(() => import('./pages/PatientAddressesTab'))
const ProviderCoverage = lazy(() => import('./pages/ProviderCoverage'))

// Encounter module pages
const EncounterCensus = lazy(() => import('./pages/EncounterCensus'))
const EncounterDetail = lazy(() => import('./pages/EncounterDetail'))
const DischargeWorkflow = lazy(() => import('./pages/DischargeWorkflow'))

// Scheduling pages
const ScheduleManagement = lazy(() => import('./pages/ScheduleManagement'))
const Waitlist = lazy(() => import('./pages/Waitlist'))

// Clinical worklist pages
const OrderCatalog = lazy(() => import('./pages/OrderCatalog'))
const OrderSets = lazy(() => import('./pages/OrderSets'))
const PendingResults = lazy(() => import('./pages/PendingResults'))
const CriticalResults = lazy(() => import('./pages/CriticalResults'))
const Formulary = lazy(() => import('./pages/Formulary'))
const PharmacistQueue = lazy(() => import('./pages/PharmacistQueue'))
const MedReconciliation = lazy(() => import('./pages/MedReconciliation'))

// Documentation pages
const NotesWorklist = lazy(() => import('./pages/NotesWorklist'))
const NoteTemplates = lazy(() => import('./pages/NoteTemplates'))
const SmartPhrases = lazy(() => import('./pages/SmartPhrases'))

// Billing pages
const BillingCharges = lazy(() => import('./pages/BillingCharges'))
const BillingClaims = lazy(() => import('./pages/BillingClaims'))
const BillingPayments = lazy(() => import('./pages/BillingPayments'))
const PatientAccounts = lazy(() => import('./pages/PatientAccounts'))
const BillingStatements = lazy(() => import('./pages/BillingStatements'))

// Analytics pages
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'))
const ReportRunner = lazy(() => import('./pages/ReportRunner'))
const QualityMeasures = lazy(() => import('./pages/QualityMeasures'))

// Admin pages
const AdminUsers = lazy(() => import('./pages/AdminUsers'))
const AdminRoles = lazy(() => import('./pages/AdminRoles'))
const AdminFacilities = lazy(() => import('./pages/AdminFacilities'))
const AdminProviders = lazy(() => import('./pages/AdminProviders'))
const AdminIntegrations = lazy(() => import('./pages/AdminIntegrations'))
const AdminAudit = lazy(() => import('./pages/AdminAudit'))
const AdminSystem = lazy(() => import('./pages/AdminSystem'))
const AdminThirdPartyApps = lazy(() => import('./pages/AdminThirdPartyApps'))

// Portal pages
const PortalLogin = lazy(() => import('./pages/PortalLogin'))
const PortalRegister = lazy(() => import('./pages/PortalRegister'))
const PortalDashboard = lazy(() => import('./pages/PortalDashboard'))
const PortalResults = lazy(() => import('./pages/PortalResults'))
const PortalAppointments = lazy(() => import('./pages/PortalAppointments'))
const PortalMessages = lazy(() => import('./pages/PortalMessages'))
const PortalForms = lazy(() => import('./pages/PortalForms'))

// New pages
const AccessDenied = lazy(() => import('./pages/AccessDenied'))
const UserProfile = lazy(() => import('./pages/UserProfile'))
const NotificationInbox = lazy(() => import('./pages/NotificationInbox'))
const VitalsEntry = lazy(() => import('./pages/VitalsEntry'))
const PatientContacts = lazy(() => import('./pages/PatientContacts'))
const ProviderDirectory = lazy(() => import('./pages/ProviderDirectory'))
const CdsRulesAdmin = lazy(() => import('./pages/CdsRulesAdmin'))
const OrderCosignQueue = lazy(() => import('./pages/OrderCosignQueue'))
const WoundCareDoc = lazy(() => import('./pages/WoundCareDoc'))

// Licensing admin pages
const AccessMatrix = lazy(() => import('./pages/AccessMatrix'))
const AdminLicenseTiers = lazy(() => import('./pages/AdminLicenseTiers'))
const AdminLicensePricing = lazy(() => import('./pages/AdminLicensePricing'))
const AdminLicenseFacilities = lazy(() => import('./pages/AdminLicenseFacilities'))
const AdminLicenseUsage = lazy(() => import('./pages/AdminLicenseUsage'))

// Admin form pages (modal-to-page conversions)
const AdminUserForm = lazy(() => import('./pages/AdminUserForm'))
const AdminProviderForm = lazy(() => import('./pages/AdminProviderForm'))
const AdminFacilityForm = lazy(() => import('./pages/AdminFacilityForm'))
const AdminHealthSystems = lazy(() => import('./pages/AdminHealthSystems'))
const AdminHealthSystemForm = lazy(() => import('./pages/AdminHealthSystemForm'))
const AdminTaxonomy = lazy(() => import('./pages/AdminTaxonomy'))
const AdminServiceLineForm = lazy(() => import('./pages/AdminServiceLineForm'))
const AdminClinicalServiceForm = lazy(() => import('./pages/AdminClinicalServiceForm'))
const AdminServiceLocations = lazy(() => import('./pages/AdminServiceLocations'))
const AdminDepartmentForm = lazy(() => import('./pages/AdminDepartmentForm'))
const AdminLocations = lazy(() => import('./pages/AdminLocations'))
const AdminLocationForm = lazy(() => import('./pages/AdminLocationForm'))
const AdminIntegrationForm = lazy(() => import('./pages/AdminIntegrationForm'))
const AdminSmartAppForm = lazy(() => import('./pages/AdminSmartAppForm'))
const AdminFhirEndpointForm = lazy(() => import('./pages/AdminFhirEndpointForm'))
const CdsRuleForm = lazy(() => import('./pages/CdsRuleForm'))
const RoleForm = lazy(() => import('./pages/RoleForm'))
const CategoryForm = lazy(() => import('./pages/CategoryForm'))
const AdminLicenseTierForm = lazy(() => import('./pages/AdminLicenseTierForm'))
const AdminLicenseAssignForm = lazy(() => import('./pages/AdminLicenseAssignForm'))
const NoteTemplateForm = lazy(() => import('./pages/NoteTemplateForm'))
const SmartPhraseForm = lazy(() => import('./pages/SmartPhraseForm'))
const ScheduleTemplateForm = lazy(() => import('./pages/ScheduleTemplateForm'))
const BillingChargeForm = lazy(() => import('./pages/BillingChargeForm'))
const BillingPaymentForm = lazy(() => import('./pages/BillingPaymentForm'))
const BillingClaimForm = lazy(() => import('./pages/BillingClaimForm'))
const DenialManagement = lazy(() => import('./pages/DenialManagement'))

// Loading component
const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="text-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="flostel-ui-theme">
        <Router>
          <AuthProvider>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />

                {/* Protected routes — require authentication, then wrapped in AppShell */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<AppShell />}>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Profile & Notifications */}
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/notifications" element={<NotificationInbox />} />

                  {/* Patient module */}
                  <Route path="/patients/search" element={<PatientSearch />} />
                  <Route path="/patients/register" element={<RequirePermission permission={PERMS.PATIENT_CREATE}><PatientRegistration /></RequirePermission>} />
                  <Route path="/patients/merge" element={<RequirePermission permission={PERMS.PATIENT_MERGE}><PatientMerge /></RequirePermission>} />

                  {/* Patient chart with shared layout (banner + tab nav) */}
                  <Route path="/patients/:patientId" element={<PatientChartLayout />}>
                    <Route index element={<PatientDemographics />} />
                    <Route path="demographics" element={<PatientDemographics />} />
                    <Route path="problems" element={<PatientProblems />} />
                    <Route path="allergies" element={<PatientAllergies />} />
                    <Route path="encounters" element={<PatientEncounters />} />
                    <Route path="orders" element={<OrderEntry />} />
                    <Route path="results" element={<ResultsReview />} />
                    <Route path="medications" element={<MedicationMAR />} />
                    <Route path="notes" element={<ClinicalNotes />} />
                    <Route path="immunizations" element={<PatientImmunizations />} />
                    <Route path="insurance" element={<PatientInsurance />} />
                    <Route path="proxies" element={<PatientProxies />} />
                    <Route path="contacts" element={<PatientContacts />} />
                    <Route path="intake-output" element={<IntakeOutput />} />
                    <Route path="care-plans" element={<CarePlanManagement />} />
                    <Route path="family-history" element={<FamilyHistoryTab />} />
                    <Route path="social-history" element={<SocialHistoryTab />} />
                    <Route path="surgical-history" element={<SurgicalHistoryTab />} />
                    <Route path="referrals" element={<ReferralManagement />} />
                    <Route path="advance-directives" element={<AdvanceDirectivesTab />} />
                    <Route path="identifiers" element={<PatientIdentifiersTab />} />
                    <Route path="addresses" element={<PatientAddressesTab />} />
                    <Route path="allergies/new" element={<PatientAllergyForm />} />
                    <Route path="problems/new" element={<PatientProblemForm />} />
                    <Route path="immunizations/new" element={<PatientImmunizationForm />} />
                    <Route path="insurance/new" element={<PatientInsuranceForm />} />
                    <Route path="contacts/new" element={<PatientContactForm />} />
                  </Route>

                  {/* Encounter module */}
                  <Route path="/encounters/census" element={<EncounterCensus />} />
                  <Route path="/encounters/:encounterId" element={<EncounterDetail />} />
                  <Route path="/encounters/:encounterId/discharge" element={<DischargeWorkflow />} />
                  <Route path="/encounters/:encounterId/vitals" element={<VitalsEntry />} />
                  <Route path="/encounters/:encounterId/wound-care" element={<WoundCareDoc />} />

                  {/* Scheduling */}
                  <Route path="/appointments" element={<Appointments />} />
                  <Route path="/appointments/new" element={<RequirePermission permission={PERMS.SCHEDULE_MANAGE}><AppointmentForm /></RequirePermission>} />
                  <Route path="/appointments/:id/edit" element={<RequirePermission permission={PERMS.SCHEDULE_MANAGE}><AppointmentForm /></RequirePermission>} />
                  <Route path="/appointments/schedule" element={<ScheduleManagement />} />
                  <Route path="/appointments/schedule/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><ScheduleTemplateForm /></RequirePermission>} />
                  <Route path="/appointments/schedule/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><ScheduleTemplateForm /></RequirePermission>} />
                  <Route path="/appointments/waitlist" element={<Waitlist />} />

                  {/* Providers */}
                  <Route path="/providers" element={<ProviderDirectory />} />
                  <Route path="/coverage" element={<ProviderCoverage />} />

                  {/* Clinical worklists */}
                  <Route path="/orders/catalog" element={<OrderCatalog />} />
                  <Route path="/orders/sets" element={<OrderSets />} />
                  <Route path="/orders/cosign-queue" element={<OrderCosignQueue />} />
                  <Route path="/results/pending" element={<PendingResults />} />
                  <Route path="/results/critical" element={<CriticalResults />} />
                  <Route path="/medications/formulary" element={<Formulary />} />
                  <Route path="/medications/pharmacist-queue" element={<PharmacistQueue />} />
                  <Route path="/patients/:patientId/encounters/:encounterId/reconciliation" element={<MedReconciliation />} />

                  {/* Documentation */}
                  <Route path="/notes/worklist" element={<NotesWorklist />} />
                  <Route path="/notes/templates" element={<NoteTemplates />} />
                  <Route path="/notes/templates/new" element={<NoteTemplateForm />} />
                  <Route path="/notes/templates/:id/edit" element={<NoteTemplateForm />} />
                  <Route path="/notes/smart-phrases" element={<SmartPhrases />} />
                  <Route path="/notes/smart-phrases/new" element={<SmartPhraseForm />} />
                  <Route path="/notes/smart-phrases/:id/edit" element={<SmartPhraseForm />} />

                  {/* Billing */}
                  <Route path="/billing/charges" element={<BillingCharges />} />
                  <Route path="/billing/charges/new" element={<RequirePermission permission={PERMS.BILLING_CREATE}><BillingChargeForm /></RequirePermission>} />
                  <Route path="/billing/claims" element={<BillingClaims />} />
                  <Route path="/billing/claims/new" element={<RequirePermission permission={PERMS.BILLING_CREATE}><BillingClaimForm /></RequirePermission>} />
                  <Route path="/billing/payments" element={<BillingPayments />} />
                  <Route path="/billing/payments/new" element={<RequirePermission permission={PERMS.BILLING_PAYMENT}><BillingPaymentForm /></RequirePermission>} />
                  <Route path="/billing/accounts" element={<PatientAccounts />} />
                  <Route path="/billing/statements" element={<BillingStatements />} />
                  <Route path="/billing/denials" element={<RequirePermission permission={PERMS.BILLING_VIEW}><DenialManagement /></RequirePermission>} />

                  {/* Analytics */}
                  <Route path="/analytics/dashboard" element={<AnalyticsDashboard />} />
                  <Route path="/analytics/reports" element={<ReportRunner />} />
                  <Route path="/analytics/quality" element={<QualityMeasures />} />

                  {/* Administration — all admin routes require USER_MANAGE or SYS_ADMIN_ALL */}
                  <Route path="/admin/users" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminUsers /></RequirePermission>} />
                  <Route path="/admin/users/new" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminUserForm /></RequirePermission>} />
                  <Route path="/admin/users/:id/edit" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminUserForm /></RequirePermission>} />
                  <Route path="/admin/roles" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminRoles /></RequirePermission>} />
                  <Route path="/admin/facilities" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminFacilities /></RequirePermission>} />
                  <Route path="/admin/facilities/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminFacilityForm /></RequirePermission>} />
                  <Route path="/admin/facilities/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminFacilityForm /></RequirePermission>} />
                  <Route path="/admin/health-systems" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminHealthSystems /></RequirePermission>} />
                  <Route path="/admin/health-systems/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminHealthSystemForm /></RequirePermission>} />
                  <Route path="/admin/health-systems/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminHealthSystemForm /></RequirePermission>} />
                  <Route path="/admin/taxonomy" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminTaxonomy /></RequirePermission>} />
                  <Route path="/admin/taxonomy/service-lines/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminServiceLineForm /></RequirePermission>} />
                  <Route path="/admin/taxonomy/service-lines/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminServiceLineForm /></RequirePermission>} />
                  <Route path="/admin/taxonomy/clinical-services/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminClinicalServiceForm /></RequirePermission>} />
                  <Route path="/admin/taxonomy/clinical-services/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminClinicalServiceForm /></RequirePermission>} />
                  <Route path="/admin/service-locations" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminServiceLocations /></RequirePermission>} />
                  <Route path="/admin/departments/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminDepartmentForm /></RequirePermission>} />
                  <Route path="/admin/departments/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminDepartmentForm /></RequirePermission>} />
                  <Route path="/admin/locations" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminLocations /></RequirePermission>} />
                  <Route path="/admin/locations/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminLocationForm /></RequirePermission>} />
                  <Route path="/admin/locations/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminLocationForm /></RequirePermission>} />
                  <Route path="/admin/providers" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminProviders /></RequirePermission>} />
                  <Route path="/admin/providers/new" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminProviderForm /></RequirePermission>} />
                  <Route path="/admin/providers/:id/edit" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminProviderForm /></RequirePermission>} />
                  <Route path="/admin/integrations" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminIntegrations /></RequirePermission>} />
                  <Route path="/admin/integrations/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminIntegrationForm /></RequirePermission>} />
                  <Route path="/admin/integrations/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminIntegrationForm /></RequirePermission>} />
                  <Route path="/admin/integrations/smart-apps/new" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminSmartAppForm /></RequirePermission>} />
                  <Route path="/admin/integrations/smart-apps/:id/edit" element={<RequirePermission permission={PERMS.USER_MANAGE}><AdminSmartAppForm /></RequirePermission>} />
                  <Route path="/admin/integrations/fhir-endpoints/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminFhirEndpointForm /></RequirePermission>} />
                  <Route path="/admin/integrations/fhir-endpoints/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminFhirEndpointForm /></RequirePermission>} />
                  <Route path="/admin/audit" element={<RequirePermission permission={PERMS.AUDIT_VIEW}><AdminAudit /></RequirePermission>} />
                  <Route path="/admin/system" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminSystem /></RequirePermission>} />
                  <Route path="/admin/third-party-apps" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><AdminThirdPartyApps /></RequirePermission>} />
                  <Route path="/admin/access-matrix" element={<RequirePermission permission={PERMS.USER_MANAGE}><AccessMatrix /></RequirePermission>} />
                  <Route path="/admin/access-matrix/roles/new" element={<RequirePermission permission={PERMS.USER_MANAGE}><RoleForm /></RequirePermission>} />
                  <Route path="/admin/access-matrix/roles/:id/edit" element={<RequirePermission permission={PERMS.USER_MANAGE}><RoleForm /></RequirePermission>} />
                  <Route path="/admin/access-matrix/categories/new" element={<RequirePermission permission={PERMS.USER_MANAGE}><CategoryForm /></RequirePermission>} />
                  <Route path="/admin/access-matrix/categories/:id/edit" element={<RequirePermission permission={PERMS.USER_MANAGE}><CategoryForm /></RequirePermission>} />
                  <Route path="/admin/cds-rules" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><CdsRulesAdmin /></RequirePermission>} />
                  <Route path="/admin/cds-rules/new" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><CdsRuleForm /></RequirePermission>} />
                  <Route path="/admin/cds-rules/:id/edit" element={<RequirePermission permission={PERMS.SYS_ADMIN_ALL}><CdsRuleForm /></RequirePermission>} />

                  {/* Licensing Administration */}
                  <Route path="/admin/licensing/tiers" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicenseTiers /></RequirePermission>} />
                  <Route path="/admin/licensing/tiers/new" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicenseTierForm /></RequirePermission>} />
                  <Route path="/admin/licensing/pricing" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicensePricing /></RequirePermission>} />
                  <Route path="/admin/licensing/facilities" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicenseFacilities /></RequirePermission>} />
                  <Route path="/admin/licensing/facilities/:facilityId/assign" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicenseAssignForm /></RequirePermission>} />
                  <Route path="/admin/licensing/usage" element={<RequirePermission permission={PERMS.LICENSING_MANAGE}><AdminLicenseUsage /></RequirePermission>} />
                  </Route>
                </Route>

                {/* Portal Routes - separate from main EMR */}
                <Route path="/portal/login" element={<PortalLogin />} />
                <Route path="/portal/register" element={<PortalRegister />} />
                <Route path="/portal" element={<PortalLayout />}>
                  <Route index element={<Navigate to="/portal/dashboard" replace />} />
                  <Route path="dashboard" element={<PortalDashboard />} />
                  <Route path="results" element={<PortalResults />} />
                  <Route path="appointments" element={<PortalAppointments />} />
                  <Route path="messages" element={<PortalMessages />} />
                  <Route path="forms" element={<PortalForms />} />
                </Route>

                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                {/* 403 Access Denied — target of RequirePermission redirects */}
                <Route path="/403" element={<AccessDenied />} />

                {/* 404 Not Found */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>

          {/* Toast notifications */}
          <Toaster position="top-right" richColors closeButton />
        </Router>
      </ThemeProvider>

      {/* React Query Devtools (only in development) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

// 404 Not Found component
function NotFound() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
        <p className="text-xl text-muted-foreground mb-6">Page not found</p>
        <a
          href="/dashboard"
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  )
}

export default App
